import React, { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { X, ShoppingBag, Clock, Info, Plus, Trash2, ChevronDown, ChevronUp } from "lucide-react";

// All products with German names hardcoded for owner's WhatsApp message
const ALL_CATEGORIES = [
  {
    keyDE: "Lamm",
    items: [
      { keyDE: "Lammkeule" },
      { keyDE: "Lammknacken" },
      { keyDE: "Lammschulter" },
      { keyDE: "Lammrippen" },
      { keyDE: "Lammkotelett" },
      { keyDE: "Lammleber" },
      { keyDE: "Lammhackfleisch" },
    ],
  },
  {
    keyDE: "Rind",
    items: [
      { keyDE: "Rinderrippen" },
      { keyDE: "Rinderklugen" },
      { keyDE: "Rinderhack (ohne Gewürz)" },
      { keyDE: "Unterkeule" },
      { keyDE: "Kalbskugel" },
    ],
  },
  {
    keyDE: "Hähnchen",
    items: [
      { keyDE: "Hähnchenbrust" },
      { keyDE: "Hähnchenkeule" },
      { keyDE: "Hähnchenleber" },
      { keyDE: "Hähnchenmagen" },
      { keyDE: "Hähnchenflügel" },
      { keyDE: "Hähnchenherz" },
    ],
  },
  {
    keyDE: "Spezial",
    items: [
      { keyDE: "Putenfleisch" },
      { keyDE: "Schaschlik" },
    ],
  },
];

export default function PreOrderModal({ product, productDE, onClose }) {
  const { t } = useLanguage();
  const [customerName, setCustomerName] = useState("");
  const [pickupDay, setPickupDay] = useState("today");
  const [pickupTime, setPickupTime] = useState("");
  const [note, setNote] = useState("");
  const [openCategory, setOpenCategory] = useState(null);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);

  // Cart starts with the clicked product
  const [cartItems, setCartItems] = useState([
    { nameDisplay: product, nameDE: productDE, quantity: "" }
  ]);

  const [editingIndex, setEditingIndex] = useState(null);

  const addToCart = (nameDisplay, nameDE) => {
    const exists = cartItems.findIndex((i) => i.nameDE === nameDE);
    if (exists !== -1) {
      setEditingIndex(exists);
      setShowProductPicker(false);
      setOpenCategory(null);
      return;
    }
    setCartItems([...cartItems, { nameDisplay: nameDE, nameDE, quantity: "" }]);
    setShowProductPicker(false);
    setOpenCategory(null);
    setEditingIndex(cartItems.length);
  };

  const removeItem = (index) => {
    if (cartItems.length === 1) return;
    setCartItems(cartItems.filter((_, i) => i !== index));
  };

  const updateQuantity = (index, value) => {
    const updated = [...cartItems];
    updated[index].quantity = value;
    setCartItems(updated);
  };

  const isValid =
    customerName &&
    pickupTime &&
    consentGiven &&
    cartItems.every((item) => item.quantity);

  const handleSend = () => {
    if (!isValid) return;

    const day = pickupDay === "today" ? "Heute" : "Morgen";
    const itemLines = cartItems
      .map((item, i) => `  ${i + 1}. ${item.nameDE} — ${item.quantity}`)
      .join("\n");

    const message =
      `*Vorbestellung — Al-Kasbah*\n\n` +
      `Kundenname: ${customerName}\n\n` +
      `Bestellte Produkte:\n${itemLines}\n\n` +
      `Abholtag: ${day}\n` +
      `Abholzeit: ${pickupTime}\n` +
      (note ? `Anmerkung: ${note}\n` : "") +
      `\nBitte bestaetigen Sie meine Bestellung. Danke!`;

    const encoded = encodeURIComponent(message);
    window.open(`https://wa.me/491798272922?text=${encoded}`, "_blank");
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="relative w-full max-w-md bg-white rounded-sm shadow-2xl"
        style={{ maxHeight: "90vh", overflowY: "auto" }}
      >
        {/* Header */}
        <div
          className="px-6 py-5 flex items-center justify-between sticky top-0 z-10"
          style={{ backgroundColor: "#8B0000" }}
        >
          <div className="flex items-center gap-3">
            <ShoppingBag className="h-5 w-5 text-white" />
            <h2 className="font-heading text-lg font-semibold text-white">
              {t("preorder.title")}
            </h2>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Notice */}
        <div
          className="px-6 py-3 flex items-start gap-2"
          style={{ backgroundColor: "#FAF6EF", borderBottom: "1px solid #E8DCC8" }}
        >
          <Info className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "#D4AF37" }} />
          <p className="text-xs leading-relaxed" style={{ color: "#6B4E2A" }}>
            {t("preorder.notice")}
          </p>
        </div>

        {/* Body */}
        <div className="px-6 py-6 space-y-5">

          {/* Customer Name */}
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold mb-1"
              style={{ color: "#8B0000" }}>
              {t("preorder.name")} *
            </label>
            <p className="text-xs text-neutral-400 mb-2">{t("preorder.nameHint")}</p>
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder={t("preorder.namePlaceholder")}
              className="w-full px-4 py-3 rounded-sm text-sm outline-none"
              style={{ border: "1px solid #E5E5E5", backgroundColor: "#FAFAFA" }}
              onFocus={(e) => (e.target.style.borderColor = "#8B0000")}
              onBlur={(e) => (e.target.style.borderColor = "#E5E5E5")}
            />
          </div>

          {/* Cart */}
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold mb-3"
              style={{ color: "#8B0000" }}>
              {t("preorder.products")} *
            </label>

            {/* Cart Items */}
            <div className="space-y-2 mb-3">
              {cartItems.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 px-4 py-3 rounded-sm"
                  style={{
                    backgroundColor: editingIndex === index ? "#FFF8F0" : "#F9F9F9",
                    border: editingIndex === index ? "1px solid #D4AF37" : "1px solid #E5E5E5"
                  }}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: "#0A0A0A" }}>
                      {item.nameDisplay}
                    </p>
                    <input
                      type="text"
                      value={item.quantity}
                      onChange={(e) => updateQuantity(index, e.target.value)}
                      placeholder={t("preorder.quantityPlaceholder")}
                      className="mt-1 w-full text-xs px-2 py-1.5 rounded-sm outline-none"
                      style={{ border: "1px solid #E5E5E5", backgroundColor: "white" }}
                      onFocus={(e) => (e.target.style.borderColor = "#8B0000")}
                      onBlur={(e) => (e.target.style.borderColor = "#E5E5E5")}
                    />
                  </div>

                  {cartItems.length > 1 && (
                    <button
                      onClick={() => removeItem(index)}
                      className="shrink-0 text-neutral-300 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Add product — category accordion */}
            <div className="rounded-sm overflow-hidden"
              style={{ border: "1px solid #E5E5E5" }}>

              <button
                onClick={() => {
                  setShowProductPicker(!showProductPicker);
                  setOpenCategory(null);
                }}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold transition-colors"
                style={{
                  backgroundColor: showProductPicker ? "#8B0000" : "white",
                  color: showProductPicker ? "white" : "#8B0000",
                }}
              >
                <span className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  {t("preorder.addItem")}
                </span>
                {showProductPicker
                  ? <ChevronUp className="h-4 w-4" />
                  : <ChevronDown className="h-4 w-4" />
                }
              </button>

              {showProductPicker && (
                <div style={{ borderTop: "1px solid #E5E5E5" }}>
                  {ALL_CATEGORIES.map((cat) => (
                    <div key={cat.keyDE}>
                      <button
                        onClick={() =>
                          setOpenCategory(openCategory === cat.keyDE ? null : cat.keyDE)
                        }
                        className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors hover:bg-neutral-50"
                        style={{ color: "#0A0A0A", borderBottom: "1px solid #F0F0F0" }}
                      >
                        {cat.keyDE}
                        {openCategory === cat.keyDE
                          ? <ChevronUp className="h-3 w-3" />
                          : <ChevronDown className="h-3 w-3" />
                        }
                      </button>

                      {openCategory === cat.keyDE && (
                        <div style={{ backgroundColor: "#FAFAFA" }}>
                          {cat.items.map((item) => {
                            const alreadyAdded = cartItems.some(
                              (c) => c.nameDE === item.keyDE
                            );
                            return (
                              <button
                                key={item.keyDE}
                                onClick={() =>
                                  !alreadyAdded && addToCart(item.keyDE, item.keyDE)
                                }
                                disabled={alreadyAdded}
                                className="w-full text-left px-6 py-2.5 text-sm flex items-center justify-between transition-colors"
                                style={{
                                  color: alreadyAdded ? "#AAAAAA" : "#0A0A0A",
                                  borderBottom: "1px solid #F0F0F0",
                                  cursor: alreadyAdded ? "not-allowed" : "pointer",
                                }}
                                onMouseEnter={(e) => {
                                  if (!alreadyAdded)
                                    e.currentTarget.style.backgroundColor = "#FFF3F3";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = "transparent";
                                }}
                              >
                                {item.keyDE}
                                {alreadyAdded && (
                                  <span className="text-xs" style={{ color: "#D4AF37" }}>
                                    ✓ {t("preorder.added")}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pickup Day */}
            <div className="mt-5">
              <label className="block text-xs uppercase tracking-wider font-semibold mb-2"
                style={{ color: "#8B0000" }}>
                {t("preorder.pickupDay")} *
              </label>
              <div className="grid grid-cols-2 gap-3">
                {["today", "tomorrow"].map((day) => (
                  <button
                    key={day}
                    onClick={() => setPickupDay(day)}
                    className="py-3 rounded-sm text-sm font-semibold transition-all"
                    style={{
                      backgroundColor: pickupDay === day ? "#8B0000" : "#F9F9F9",
                      color: pickupDay === day ? "white" : "#0A0A0A",
                      border: pickupDay === day ? "1px solid #8B0000" : "1px solid #E5E5E5",
                    }}
                  >
                    📅 {t(`preorder.${day}`)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Pickup Time */}
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold mb-2"
              style={{ color: "#8B0000" }}>
              <Clock className="h-3 w-3 inline mr-1" />
              {t("preorder.pickupTime")} *
            </label>
            <input
              type="text"
              value={pickupTime}
              onChange={(e) => setPickupTime(e.target.value)}
              placeholder={t("preorder.pickupPlaceholder")}
              className="w-full px-4 py-3 rounded-sm text-sm outline-none"
              style={{ border: "1px solid #E5E5E5", backgroundColor: "#FAFAFA" }}
              onFocus={(e) => (e.target.style.borderColor = "#8B0000")}
              onBlur={(e) => (e.target.style.borderColor = "#E5E5E5")}
            />
          </div>

          {/* Note */}
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold mb-2"
              style={{ color: "#6B6B6B" }}>
              {t("preorder.note")}
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("preorder.notePlaceholder")}
              rows={2}
              className="w-full px-4 py-3 rounded-sm text-sm outline-none resize-none"
              style={{ border: "1px solid #E5E5E5", backgroundColor: "#FAFAFA" }}
              onFocus={(e) => (e.target.style.borderColor = "#8B0000")}
              onBlur={(e) => (e.target.style.borderColor = "#E5E5E5")}
            />
          </div>

          {/* WhatsApp Consent Checkbox (DSGVO compliance) */}
          <div
            className="flex items-start gap-3 px-4 py-3 rounded-sm"
            style={{ backgroundColor: "#FAF6EF", border: "1px solid #E8DCC8" }}
          >
            <input
              type="checkbox"
              id="whatsapp-consent"
              checked={consentGiven}
              onChange={(e) => setConsentGiven(e.target.checked)}
              className="mt-1 shrink-0 cursor-pointer"
              style={{ accentColor: "#8B0000" }}
            />
            <label
              htmlFor="whatsapp-consent"
              className="text-xs leading-relaxed cursor-pointer"
              style={{ color: "#6B4E2A" }}
            >
              {t("preorder.consent")}{" "}
              <button
  type="button"
  onClick={(e) => {
    e.preventDefault();
    window.dispatchEvent(new CustomEvent("openLegalDoc", { detail: "datenschutz" }));
  }}
  className="underline font-semibold"
  style={{ color: "#8B0000", background: "none", border: "none", padding: 0, cursor: "pointer" }}
>
  {t("preorder.consentLink")}
</button>
            </label>
          </div>

          {/* Send Button */}
          <button
            onClick={handleSend}
            disabled={!isValid}
            className="w-full py-3 rounded-sm text-white font-semibold text-sm flex items-center justify-center gap-2"
            style={{
              backgroundColor: "#25D366",
              opacity: !isValid ? 0.5 : 1,
              cursor: !isValid ? "not-allowed" : "pointer",
            }}
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            {t("preorder.send")}
          </button>

        </div>
      </div>
    </div>
  );
}